/**
 * The dialogs the opportunities/pipeline screens open: the New/Edit
 * Opportunity form, delete confirmation, the stage-change dialog (shared by
 * the list's row menu and the pipeline board's drag-and-drop — closing as
 * lost needs a reason either way), and the not-built explanation dialog.
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { money } from '../../utils/formatters.js';

const BLANK = {
  name: '', account_id: '', pipeline_id: '', stage_id: '', amount: '', currency: 'USD',
  expected_close_date: '', owner_id: '', next_step: '',
};

function validateOpportunity(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Deal name is required.';
  if (!form.account_id) errors.account_id = 'Choose an account.';
  if (!form.pipeline_id) errors.pipeline_id = 'Choose a pipeline.';
  if (!form.stage_id) errors.stage_id = 'Choose a stage.';
  if (!form.expected_close_date) errors.expected_close_date = 'Set an expected close date.';
  if (form.amount !== '' && Number(form.amount) < 0) errors.amount = 'Amount cannot be negative.';
  return errors;
}

/** Create an opportunity, or edit one. `opportunity` (an `opportunityShape.toRow()` row) switches the mode. */
export function OpportunityFormModal({ open, onClose, onSubmit, busy, options, opportunity, defaultPipelineId }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const editing = Boolean(opportunity);

  useEffect(() => {
    if (!open) return;
    if (opportunity) {
      setForm({
        name: opportunity.name ?? '',
        account_id: opportunity.account?.id ?? '',
        pipeline_id: opportunity.pipeline?.id ?? '',
        stage_id: opportunity.stage?.id ?? '',
        amount: opportunity.amount ?? '',
        currency: opportunity.currency ?? 'USD',
        expected_close_date: opportunity.expectedCloseDate ?? '',
        owner_id: opportunity.owner?.id ?? '',
        next_step: opportunity.nextStep ?? '',
      });
    } else {
      setForm({ ...BLANK, pipeline_id: defaultPipelineId ?? '' });
    }
    setErrors({});
  }, [open, opportunity, defaultPipelineId]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value, ...(k === 'pipeline_id' ? { stage_id: '' } : {}) }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const stagesForPipeline = useMemo(
    () => options.stages.filter((s) => s.pipelineId === form.pipeline_id).sort((a, b) => a.sortOrder - b.sortOrder),
    [options.stages, form.pipeline_id],
  );

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validateOpportunity(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (editing) {
      const payload = {};
      for (const k of ['name', 'account_id', 'amount', 'currency', 'expected_close_date', 'owner_id', 'next_step']) {
        if (form[k] === '') continue;
        payload[k] = k === 'amount' ? Number(form[k]) : form[k];
      }
      onSubmit(payload);
    } else {
      onSubmit({
        name: form.name,
        account_id: form.account_id,
        pipeline_id: form.pipeline_id,
        stage_id: form.stage_id,
        amount: form.amount === '' ? undefined : Number(form.amount),
        currency: form.currency,
        expected_close_date: form.expected_close_date,
        owner_id: form.owner_id || undefined,
        next_step: form.next_step || undefined,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Opportunity' : 'New Opportunity'}
      size="lg"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="oppForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save opportunity'}
          </button>
        </>
      )}
    >
      <form id="oppForm" onSubmit={submit} noValidate>
        <div className="row g-3 mb-3">
          <div className="col-md-8">
            <Field label="Deal name" required error={errors.name}>
              <input className="form-control" value={form.name} onChange={set('name')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Owner">
              <Picker value={form.owner_id} onChange={set('owner_id')} options={options.owners} blank="Unassigned" loading={options.loading} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Account" required error={errors.account_id}>
              <Picker value={form.account_id} onChange={set('account_id')} options={options.accounts} blank="Choose an account…" loading={options.loading} />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Pipeline" required error={errors.pipeline_id}>
              <Picker
                value={form.pipeline_id} onChange={set('pipeline_id')} options={options.pipelines}
                blank="Choose a pipeline…" disabled={editing} loading={options.loading}
              />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Stage" required error={errors.stage_id}>
              <Picker
                value={form.stage_id} onChange={set('stage_id')} options={stagesForPipeline}
                blank={!options.loading && form.pipeline_id ? 'Choose a stage…' : 'Choose a pipeline first'}
                disabled={editing || !form.pipeline_id} loading={options.loading}
              />
            </Field>
            {editing && <div className="t-2xs ink-4 mt-1">Use the stage menu on the deal to move it.</div>}
          </div>
          <div className="col-md-4">
            <Field label="Amount" error={errors.amount}>
              <input className="form-control" type="number" min="0" step="1000" value={form.amount} onChange={set('amount')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Currency">
              <input className="form-control" maxLength={3} value={form.currency} onChange={(e) => set('currency')({ target: { value: e.target.value.toUpperCase() } })} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Expected close date" required error={errors.expected_close_date}>
              <input className="form-control" type="date" value={form.expected_close_date} onChange={set('expected_close_date')} />
            </Field>
          </div>
          <div className="col-12">
            <Field label="Next step" help="What has to happen for this deal to move forward.">
              <input className="form-control" value={form.next_step} onChange={set('next_step')} />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Options come from `useOpportunityOptions()`'s master-data queries, which can
 * still be in flight when a fast click opens this modal. Without `loading`,
 * an empty list and a genuinely-empty one render identically — "not
 * populated" is indistinguishable from "still fetching" — so a still-loading
 * picker says so instead of showing a bare, indistinguishable blank option.
 */
const Picker = ({ value, onChange, options, blank = 'Not set', disabled = false, loading = false }) => (
  <select className="form-select" value={value} onChange={onChange} disabled={disabled || (loading && options.length === 0)}>
    <option value="">{loading && options.length === 0 ? 'Loading…' : blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/** Delete, with the reason the database insists on. */
export function DeleteOpportunityModal({ open, opportunity, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete opportunity"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{opportunity?.name}</strong> ({opportunity?.opportunityNo}).
        The row stays in the database with <code>deleted_at</code> set.
      </p>
      <Field label="Reason" required help="Written to the audit trail.">
        <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate of OP-00090" autoFocus />
      </Field>
    </Modal>
  );
}

/**
 * Move a deal to another stage. Closing as lost (the target stage's
 * `isClosed && !isWon`) switches the form to require a reason — the same
 * `422 lost_reason_required` the server enforces, asked up front instead of
 * surfacing as a rejected drop.
 */
export function StageChangeModal({ open, opportunity, targetStage, onClose, onConfirm, busy, options }) {
  const [lostReasonId, setLostReasonId] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [lostCompetitorId, setLostCompetitorId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) { setLostReasonId(''); setLostNote(''); setLostCompetitorId(''); setNote(''); }
  }, [open, targetStage?.value]);

  const closingLost = Boolean(targetStage?.isClosed) && !targetStage?.isWon;
  const closingWon = Boolean(targetStage?.isClosed) && Boolean(targetStage?.isWon);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={closingLost ? 'Close as lost' : closingWon ? 'Close as won' : `Move to ${targetStage?.label ?? '…'}`}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-brand btn-sm-x"
            style={closingLost ? { background: 'var(--rose)', borderColor: 'transparent' } : undefined}
            disabled={busy || (closingLost && !lostReasonId)}
            onClick={() => onConfirm({
              stage_id: targetStage.value,
              note: note || undefined,
              ...(closingLost ? { lost_reason_id: lostReasonId, lost_note: lostNote || undefined, lost_to_competitor_id: lostCompetitorId || undefined } : {}),
            })}
          >
            {busy ? <><ButtonSpinner />Moving…</> : closingLost ? 'Close as lost' : closingWon ? 'Close as won' : 'Move'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Moving <strong>{opportunity?.name}</strong> ({money(opportunity?.amount, opportunity?.currency)}) to{' '}
        <strong>{targetStage?.label}</strong>.
      </p>

      {targetStage?.exitCriteria && !closingLost && !closingWon && (
        <div className="t-xs p-2 px-3 mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 'var(--r)' }}>
          <i className="bi bi-info-circle me-1" aria-hidden="true" />Exit criteria for this stage: {targetStage.exitCriteria}
        </div>
      )}

      {closingLost && (
        <>
          <Field label="Lost reason" required help="The database rejects the move without one — loss analysis depends on it.">
            <select className="form-select" value={lostReasonId} onChange={(e) => setLostReasonId(e.target.value)}>
              <option value="">Choose a reason…</option>
              {options.lostReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <Field label="Lost to (competitor)">
                <select className="form-select" value={lostCompetitorId} onChange={(e) => setLostCompetitorId(e.target.value)}>
                  <option value="">Not applicable</option>
                  {options.competitors.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Note">
                <input className="form-control" value={lostNote} onChange={(e) => setLostNote(e.target.value)} placeholder="What happened" />
              </Field>
            </div>
          </div>
        </>
      )}

      {!closingLost && (
        <Field label="Note" help="Recorded on the stage-history entry.">
          <input className="form-control" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" autoFocus />
        </Field>
      )}
    </Modal>
  );
}

/** Why a header action is disabled, in a dialog rather than a tooltip — same pattern as leads/accounts/contacts. */
export function NotBuiltModal({ open, action, onClose }) {
  if (!action) return null;
  return (
    <Modal
      open={open} onClose={onClose} title={action.title}
      footer={<button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Close</button>}
    >
      <p className="t-sm ink-2">{action.why}</p>
      <div className="t-xs ink-3">Endpoint: <code>{action.endpoint}</code> — {action.section}</div>
    </Modal>
  );
}
